import type { ReactNode } from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '../../cn';

interface EmptyStateProps {
  /** Illustrative icon; defaults to an inbox glyph. */
  icon?: LucideIcon;
  /** Primary line — already localized by the caller. */
  title: string;
  /** Optional secondary explanation. */
  description?: string;
  /** Optional call-to-action (e.g. a "Create" button). */
  action?: ReactNode;
  className?: string;
}

/**
 * Canonical empty-state primitive: icon + title + optional description
 * and action, centered. Replaces the ad-hoc `text-muted-foreground`
 * one-liners scattered across list/card pages so "there is nothing to
 * show" reads the same everywhere (#1290).
 *
 * Renders no Card wrapper of its own — callers that already sit inside
 * a `<Card>` keep their frame; standalone callers can add one.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      role="status"
      className={cn('flex flex-col items-center justify-center gap-3 py-12 text-center', className)}
    >
      <Icon className="w-10 h-10 text-muted-foreground/60" aria-hidden="true" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
